import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { RouterProvider } from "react-router-dom";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "sonner";

import "./app.css";

import { initTravelixFirebase } from "@travelix/core";
import { firebaseConfig } from "./lib/firebase";
import { queryClient } from "./lib/query";
import { router } from "./lib/router";
import { ThemeProvider } from "./lib/theme-provider";
import { AuthProvider } from "./lib/auth-provider";
import { initCapacitorGlue } from "./lib/capacitor-glue";
import { initAnalytics } from "./lib/analytics";
import { installGlobalErrorHandlers } from "./lib/crashlytics";
import { InstallPrompt } from "./components/install-prompt";
import { UpdatePrompt } from "./components/update-prompt";

// Boot Firebase before React renders — the AuthProvider assumes it's ready.
initTravelixFirebase(firebaseConfig);

// Analytics + crash reporting — independent of the native-shell glue below,
// so they also run on the web/iOS PWA build.
void initAnalytics();
installGlobalErrorHandlers();

// Wire native-shell plugins (splash, status bar, hardware back).
// No-op on the web build; guarded internally by Capacitor.isNativePlatform().
void initCapacitorGlue();

const el = document.getElementById("root");
if (!el) throw new Error("Root element #root not found in index.html");

createRoot(el).render(
  <StrictMode>
    <ThemeProvider>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <RouterProvider router={router} />
          {/* Status-bar scrim. The app draws edge-to-edge on Android (and
              under the notch on iOS PWA), so without this, content scrolls
              up behind the system clock/icons. Same colour as the page so
              it reads as the status bar's background. */}
          <div
            aria-hidden
            className="pointer-events-none fixed inset-x-0 top-0 z-[45] bg-bg"
            style={{ height: "var(--safe-top)" }}
          />
          <InstallPrompt />
          <UpdatePrompt />
          <Toaster
            position="top-center"
            richColors
            closeButton
            // Toasts are portalled to <body> with position:fixed, so the body's
            // padding-top: var(--safe-top) doesn't push them down —
            // fixed positioning is viewport-relative. Add the inset directly
            // to the toaster's own offset so it clears the notch / status bar
            // on Capacitor Android + iOS PWA. env(...) resolves to 0 on
            // desktop, so this is a no-op on the web build.
            offset={{ top: "calc(var(--safe-top) + 16px)" }}
            mobileOffset={{ top: "calc(var(--safe-top) + 16px)" }}
            toastOptions={{
              className: "font-sans",
              style: {
                background: "var(--bg-elevated)",
                border: "1px solid var(--line)",
                color: "var(--ink)",
                boxShadow: "var(--shadow-3)",
                borderRadius: "var(--radius-md)",
              },
            }}
          />
        </AuthProvider>
      </QueryClientProvider>
    </ThemeProvider>
  </StrictMode>,
);
